<template>
  <div 
    class="controls-panel no-print" 
    :class="{ 'controls-collapsed': !expanded }"
  >
    <div class="controls-content">      
      <!-- Expanded state - full controls -->
      <div v-if="expanded" class="controls-expanded-content">
        <v-row align="center" no-gutters class="controls-row">
          <!-- Paper Format -->
          <v-col cols="auto" class="control-item">
            <v-select
              :model-value="paperFormat"
              @update:model-value="$emit('update:paperFormat', $event)"
              :items="paperFormats"
              label="Paper"
              density="compact"
              hide-details
              variant="outlined"
              style="min-width: 100px;"
            ></v-select>
          </v-col>
          
          <!-- Columns -->
          <v-col cols="auto" class="control-item">
            <v-btn-toggle 
              :model-value="columnCount" 
              @update:model-value="$emit('update:columnCount', $event)"
              mandatory 
              size="small" 
              density="compact"
            >
              <v-btn :value="1" size="small">1</v-btn>
              <v-btn :value="2" size="small">2</v-btn>
              <v-btn :value="3" size="small">3</v-btn>
              <v-btn :value="4" size="small">4</v-btn>
            </v-btn-toggle>
          </v-col>
          
          <!-- Toggle Options -->
          <v-col cols="auto" class="control-item">
            <v-checkbox
              :model-value="showImages"
              @update:model-value="$emit('update:showImages', $event)"
              label="Images"
              density="compact"
              hide-details
            ></v-checkbox>
          </v-col>
          
          <v-col cols="auto" class="control-item">
            <v-checkbox
              :model-value="showTitle"
              @update:model-value="$emit('update:showTitle', $event)"
              label="Title"
              density="compact"
              hide-details
            ></v-checkbox>
          </v-col>
          
          <v-col cols="auto" class="control-item">
            <v-checkbox
              :model-value="showDescription"
              @update:model-value="$emit('update:showDescription', $event)"
              label="Description"
              density="compact"
              hide-details
            ></v-checkbox>
          </v-col>
          
          <v-col cols="auto" class="control-item">
            <v-checkbox
              :model-value="showCheckedItems"
              @update:model-value="$emit('update:showCheckedItems', $event)"
              label="Checked"
              density="compact"
              hide-details
            ></v-checkbox>
          </v-col>
          
          <v-col cols="auto" class="control-item">
            <v-checkbox
              :model-value="showMetadata"
              @update:model-value="$emit('update:showMetadata', $event)"
              label="Metadata"
              density="compact"
              hide-details
            ></v-checkbox>
          </v-col>
          
          <v-col cols="auto" class="control-item" v-if="hasHeaderImage">
            <v-checkbox
              :model-value="showHeaderImage"
              @update:model-value="$emit('update:showHeaderImage', $event)"
              label="Header Image"
              density="compact"
              hide-details
            ></v-checkbox>
          </v-col>
          
          <v-col cols="auto" class="control-item" v-if="hasHeaderImage && showHeaderImage">
            <div class="opacity-control">
              <label class="opacity-label">Opacity</label>
              <v-slider
                :model-value="headerImageOpacity"
                @update:model-value="$emit('update:headerImageOpacity', $event)"
                min="10"
                max="100"
                step="10"
                density="compact"
                hide-details
                style="min-width: 80px; max-width: 120px;"
                thumb-label
              ></v-slider>
            </div>
          </v-col>
          
          <!-- Action Buttons -->
          <v-col cols="auto" class="control-item">
            <v-btn @click="$emit('exportPDF')" color="success" size="small" prepend-icon="mdi-file-pdf-box" :loading="exportingPDF">
              PDF
            </v-btn>
          </v-col>
          
          <v-col cols="auto" class="control-item">
            <v-btn @click="handleCollapse" variant="outlined" size="small" prepend-icon="mdi-close">
              Close
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PrintControls',
  props: {
    expanded: {
      type: Boolean,
      default: false
    },
    paperFormat: {
      type: String,
      default: 'A4'
    },
    paperFormats: {
      type: Array,
      default: () => [
        { title: 'A4', value: 'A4' },
        { title: 'Letter', value: 'Letter' },
        { title: 'Legal', value: 'Legal' }
      ]
    },
    columnCount: {
      type: Number,
      default: 4
    },
    showImages: {
      type: Boolean,
      default: true
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    showDescription: {
      type: Boolean,
      default: true
    },
    showCheckedItems: {
      type: Boolean,
      default: true
    },
    showMetadata: {
      type: Boolean,
      default: false
    },
    showHeaderImage: {
      type: Boolean,
      default: true
    },
    headerImageOpacity: {
      type: Number,
      default: 50
    },
    hasHeaderImage: {
      type: Boolean,
      default: false
    },
    exportingPDF: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'update:expanded',
    'update:paperFormat',
    'update:columnCount',
    'update:showImages',
    'update:showTitle',
    'update:showDescription',
    'update:showCheckedItems',
    'update:showMetadata',
    'update:showHeaderImage',
    'update:headerImageOpacity',
    'exportPDF'
  ],
  setup(props, { emit }) {
    const handleCollapse = () => {
      emit('update:expanded', false)
    }

    return {
      handleCollapse
    }
  }
}
</script>

<style scoped>
/* Docked Controls Panel */
.controls-panel {
  position: fixed;
  bottom: 20px;
  right: 160px; /* Position to the left of the dock */
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.controls-content {
  padding: 8px;
}

.controls-expanded-content {
  min-width: 800px;
}

.controls-row {
  gap: 16px;
}

.control-item {
  flex: 0 0 auto;
}

.control-item:not(:last-child) {
  margin-right: 8px;
}

.opacity-control {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100px;
}

.opacity-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

/* Responsive design for screen */
@media screen and (max-width: 1200px) {
  .controls-expanded-content {
    min-width: 600px;
  }
}

@media screen and (max-width: 768px) {
  .controls-panel {
    bottom: 15px;
    right: 120px; /* Adjust for smaller mobile dock */
  }
  
  .controls-expanded-content {
    min-width: 400px;
  }
  
  .controls-row {
    flex-wrap: wrap;
    gap: 8px;
  }
}

@media screen and (max-width: 480px) {
  .controls-panel {
    bottom: 15px;
    left: 10px;
    right: 10px;
    transform: none;
    width: auto;
  }
  
  .controls-expanded-content {
    min-width: 280px;
  }
  
  .controls-row {
    justify-content: center;
  }
}
</style>